import { useState } from "react";
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
} from "reactstrap";

import {
    FiActivity,
    FiArrowRight,
    FiBarChart2,
    FiCheckCircle,
    FiChevronRight,
    FiCloud,
    FiCode,
    FiCpu,
    FiDatabase,
    FiEye,
    FiFileText,
    FiLayers,
    FiMonitor,
    FiPlayCircle,
    FiServer, 
    FiUploadCloud,
    FiZap,
} from "react-icons/fi";

import GetInTouch from "../../components/common/Contact/GetInTouch";

import "./BrainStrokePredictionProjectPage.css";
import SEO from "../../components/common/seo/SEO";


const BrainStrokePredictionProjectPage = () => {
    const [openFaq, setOpenFaq] = useState("");

    const toggleFaq = (id) => {
        setOpenFaq(openFaq === id ? "" : id);
    };

    const prerequisites = [
        {
            icon: <FiCode />,
            title: "Python Programming",
            description:
                "Control flow, functions, and input/output concepts.",
        },
        {
            icon: <FiEye />,
            title: "OpenCV Basics",
            description:
                "Image reading, filtering, and morphological operations.",
        },
        {
            icon: <FiCpu />,
            title: "Machine Learning",
            description:
                "Supervised learning and CNN fundamentals.",
        },
        {
            icon: <FiDatabase />,
            title: "Data Handling",
            description:
                "NumPy arrays and pandas DataFrames.",
        },
        {
            icon: <FiLayers />,
            title: "Neuroanatomy",
            description:
                "Brain regions and MRI scan modalities. Optional.",
        },
        {
            icon: <FiFileText />,
            title: "Biomedical Imaging",
            description:
                "DICOM and NIfTI formats for further extension. Optional.",
        },
    ];

    const technologies = [
        {
            icon: <FiEye />,
            title: "Core Libraries",
            value: "OpenCV",
        },
        {
            icon: <FiCpu />,
            title: "Deep Learning",
            value: "TensorFlow / Keras",
        },
        {
            icon: <FiBarChart2 />,
            title: "Machine Learning",
            value: "Scikit-learn",
        },
        {
            icon: <FiDatabase />,
            title: "Data Processing",
            value: "NumPy",
        },
        {
            icon: <FiActivity />,
            title: "Visualization",
            value: "Matplotlib / Plotly",
        },
        {
            icon: <FiMonitor />,
            title: "Environment",
            value: "Jupyter / Google Colab",
        },
        {
            icon: <FiCloud />,
            title: "Deployment",
            value: "Azure / AWS",
        },
        {
            icon: <FiLayers />,
            title: "Datasets",
            value: "Br35H / ATLAS / NIH",
        },
    ];

    const learningObjectives = [
        "Understand the fundamentals of medical image analysis using MRI scans.",
        "Learn how to preprocess and enhance medical images using OpenCV.",
        "Gain hands-on experience in building CNN-based deep learning models for classification tasks.",
        "Train, validate, and evaluate models for stroke prediction using real-world datasets.",
        "Explore visualization techniques to interpret model performance and predictions.",
        "Understand the pipeline for deploying AI solutions in a healthcare context using cloud platforms.",
    ];

    const modules = [
        {
            number: "01",
            color: "blue",
            title: "Introduction & Setup",
            duration: "1 Hour",
            objective:
                "Set up the development environment and understand project goals.",
            topics: [
                "Project walkthrough & clinical importance",
                "Introduction to datasets (Br35H, ATLAS)",
                "Set up Google Colab with OpenCV & TensorFlow",
                "Review MRI modalities and preprocessing needs",
            ],
            deliverable:
                "Configured workspace with datasets loaded",
        },
        {
            number: "02",
            color: "green",
            title: "MRI Preprocessing",
            duration: "2 Hours",
            objective:
                "Prepare raw MRI scans for analysis using image processing.",
            topics: [
                "OpenCV image pipelines",
                "Skull stripping using morphological operations",
                "Apply CLAHE for contrast enhancement",
                "Gaussian blur & noise filtering",
            ],
            deliverable:
                "Preprocessed MRI dataset",
        },
        {
            number: "03",
            color: "blue",
            title: "Feature Engineering",
            duration: "2 Hours",
            objective:
                "Extract meaningful features from MRI for model input.",
            topics: [
                "Feature types: Edges (Canny), textures (GLCM), histograms",
                "Resize, crop, and normalize images",
                "Data augmentation (rotation, flips, shifts)",
                "Label encoding and data split (train/val/test)",
            ],
            deliverable:
                "Feature matrix + augmented training dataset",
        },
        {
            number: "03",
            color: "blue",
            title: "Feature Engineering",
            duration: "2 Hours",
            objective:
                "Extract meaningful features from MRI for model input.",
            topics: [
                "Feature types: Edges (Canny), textures (GLCM), histograms",
                "Resize, crop, and normalize images",
                "Data augmentation (rotation, flips, shifts)",
                "Label encoding and data split (train/val/test)",
            ],
            deliverable:
                "Feature matrix + augmented training dataset",
        },
        {
            number: "04",
            color: "green",
            title: "Model Development",
            duration: "3 Hours",
            objective:
                "Train a CNN model to classify stroke vs. non-stroke.",
            topics: [
                "CNN architecture overview",
                "Model building using Keras (Conv2D, MaxPooling, Flatten)",
                "Compile and train with binary cross-entropy",
                "Evaluate performance (accuracy, sensitivity, AUC)",
            ],
            deliverable:
                "Trained CNN model with evaluation metrics",
        },
        {
            number: "05",
            color: "blue",
            title: "Dashboard & Explainability",
            duration: "2 Hours",
            objective:
                "Build a diagnostic UI and add explainability tools.",
            topics: [
                "Design prediction interface with Streamlit",
                "Upload image, run inference, display result",
                "Implement Grad-CAM or heatmaps for explainability",
                "Overlay attention maps on MRI",
            ],
            deliverable:
                "Interactive dashboard with explainable AI",
        },
        {
            number: "06",
            color: "green",
            title: "Testing & Deployment",
            duration: "1 Hour",
            objective:
                "Finalize and deploy the model for clinical assistance.",
            topics: [
                "Run end-to-end testing on new MRI samples",
                "Save model in HDF5 format",
                "Deploy via Streamlit/Flask on local/cloud server",
                "Discuss integration into clinical workflows",
            ],
            deliverable:
                "Deployable AI tool for stroke prediction",
        },
    ];

    const faqs = [
        {
            id: "faq-1",
            question:
                "I’ve wasted money on courses before. How is this different?",
            answer:
                "This is not just theory. You’ll build a fully working AI-powered IT support automation system — step-by-step, guided by real professionals. It’s practical, project-based, and portfolio-ready.",
        },
        {
            id: "faq-2",
            question:
                "I’m not confident in coding yet. Can I still join?",
            answer:
                "If you’ve completed some basic programming (especially through our beginner courses), you’re good to go! If not, start with our foundation courses, then return for this project.",
        },
        {
            id: "faq-3",
            question:
                "Is this project useful for job interviews or placements?",
            answer:
                "Absolutely. You’ll finish with a deployable IT automation project, integrated with GitHub and real-time logging — a great addition to your resume and GitHub portfolio.",
        },
        {
            id: "faq-4",
            question: "Is this course beginner-friendly?",
            answer:
                "It’s beginner-friendly if you’ve completed basic to intermediate programming. If not, take our intro courses first — they’re designed to prepare you for this exact project.",
        },
        {
            id: "faq-5",
            question: "What tools do I need before starting?",
            answer:
                "Just install Python, Node.js, and VS Code — we’ll guide you through setup in the first session. Everything else will be covered during the course.",
        },
        {
            id: "faq-6",
            question: "Is this really worth my time?",
            answer:
                "In just 10 focused hours, you’ll learn email automation, GitHub API, WebSocket, and React integration — tools used by real tech teams today.",
        },
        {
            id: "faq-7",
            question: "Will I receive a certificate after the course?",
            answer:
                "Yes, you will receive an industry-recognized certificate.",
        },
        {
            id: "faq-8",
            question:
                "Are you looking to hire candidates for companies?",
            answer:
                "Yes, we specialize in helping businesses find and hire the right talent. Our HR and recruitment services are tailored to meet your company’s specific hiring needs. Contact us to learn how we can support your hiring process.",
        },
    ];

    const relatedProjects = [
        {
            icon: <FiServer />,
            title: "IT Support Agentic AI",
            description:
                "Build an AI-powered IT support automation system that processes email-based IT requests, identifies intents (e.g., GitHub access/revoke), creates tickets in a ticketing system (e.g., mock ADO/ServiceNow), performs GitHub actions, and displays activity logs in a frontend.",
            link:
                "/replace-it-help-desk-with-automated-ticketing-system-using-ai",
        },
        {
            icon: <FiActivity />,
            title:
                "Intelligent Manufacturing Quality Control System For Tire Production",
            description:
                "Developed an AI-powered quality control system for tyre manufacturing that monitors production line videos in real-time. The system uses computer vision to analyze manufacturing video segments and compares them against ground truth data to identify deviations in the production sequence. When delays or anomalies are detected at any manufacturing step, a GenAI pipeline is triggered to perform root cause analysis and provide reasoning for the delays. The solution is hosted on AWS cloud infrastructure and helps manufacturers maintain optimal production efficiency and quality standards.",
            link:
                "https://feesworth.com/ai-ml-project-intelligent-manufacturing-quality-control-system-for-tire-production/",
        },
        {
            icon: <FiLayers />,
            title:
                "Image Classification Using Naive & Deep Learning Techniques",
            description:
                "This project focuses on developing an efficient image classification system using a classical computer vision pipeline. The classification is performed using Support Vector Machines (SVM) after extracting meaningful features from input images using the Histogram of Oriented Gradients (HOG) descriptor.",
            link:
                "/ai-ml-project-image-classification-deep-learning",
        },
        {
            icon: <FiDatabase />,
            title: "Rag (Retrieval Argument Generation)",
            description:
                "Optimized large language model outputs by integrating authoritative external knowledge bases, enhancing accuracy and relevance beyond pre-trained data sources.",
            link:
                "/ai-ml-project-rag-retrieval-argument-generation",
        },
    ];

    return (
<>
<SEO
    title="Brain Stroke Prediction Project | OpenCV & CNN | FeesWorth"
    description="Build a Brain Stroke Prediction project using Python, OpenCV, CNN, TensorFlow, Keras and MRI datasets. Learn medical image preprocessing, deep learning classification, model evaluation and deployment."
    keywords="Brain Stroke Prediction Project, Brain Stroke Detection using CNN, OpenCV CNN Project, Medical Image Processing Project, MRI Image Classification, Python AI Project, Deep Learning Project, TensorFlow CNN Project, Machine Learning Project"
    canonical="https://feesworth.com/ai-ml-project-brain-stroke-prediction-opencv-cnn"
/>

        <div className="brain-stroke-page">

            {/* =====================================================
                HERO
            ===================================================== */}
            <section className="brain-stroke-hero">
                <div className="brain-stroke-hero-grid" />

                <Container>
                    <Row className="align-items-center">

                        <Col lg="7">
                            <div className="brain-stroke-hero-content">

                                <Badge className="brain-stroke-eyebrow">
                                    AI / ML PROJECT
                                </Badge>

                                <h1>
                                    Brain Stroke Prediction
                                    <span>
                                        using OpenCV & CNN
                                    </span>
                                </h1>

                                <p className="brain-stroke-hero-description">
                                    Build an automated deep learning system
                                    to classify MRI brain images and predict
                                    strokes using OpenCV, CNNs and Python.
                                </p>

                                <div className="brain-stroke-hero-tags">

                                    <span>
                                        <FiCpu />
                                        CNN
                                    </span>

                                    <span>
                                        <FiEye />
                                        OpenCV
                                    </span>

                                    <span>
                                        <FiCode />
                                        Python
                                    </span>

                                    <span>
                                        <FiDatabase />
                                        MRI Dataset
                                    </span>

                                    <span>
                                        <FiBarChart2 />
                                        Deep Learning
                                    </span>

                                </div>

                                <div className="brain-stroke-hero-actions">

                                    <Button
                                        color="primary"
                                        className="brain-stroke-primary-btn"
                                        href="#brain-stroke-contact"
                                    >
                                        Let Us Call You
                                        <FiArrowRight />
                                    </Button>

                                    <Button
                                        outline
                                        className="brain-stroke-secondary-btn"
                                        href="#brain-stroke-curriculum"
                                    >
                                        <FiPlayCircle />
                                        Explore Project
                                    </Button>

                                </div>

                                <div className="brain-stroke-hero-meta">

                                    <div>
                                        <strong>11 Hours</strong>
                                        <span>Focused Training</span>
                                    </div>

                                    <div>
                                        <strong>6 Modules</strong>
                                        <span>Hands-on Learning</span>
                                    </div>

                                    <div>
                                        <strong>CNN Project</strong>
                                        <span>Portfolio Ready</span>
                                    </div>

                                </div>

                            </div>
                        </Col>

                        <Col lg="5">

                            <div className="brain-stroke-visual">

                                <div className="brain-visual-header">
                                    <div>
                                        <span className="brain-live-dot" />
                                        MRI ANALYSIS
                                    </div>

                                    <FiActivity />
                                </div>

                                <div className="brain-scan-area">

                                    <div className="brain-scan-circle">
                                        <div className="brain-scan-inner">
                                            <span />
                                            <span />
                                            <span />
                                        </div>

                                        <div className="brain-scan-cross horizontal" />
                                        <div className="brain-scan-cross vertical" />

                                        <div className="brain-scan-focus">
                                            <FiActivity />
                                        </div>
                                    </div>

                                    <div className="brain-scan-label">
                                        CNN INFERENCE
                                    </div>

                                </div>

                                <div className="brain-visual-pipeline">

                                    <div>
                                        <FiUploadCloud />
                                        <span>Upload MRI</span>
                                    </div>

                                    <FiChevronRight />

                                    <div>
                                        <FiEye />
                                        <span>Preprocess</span>
                                    </div>

                                    <FiChevronRight />

                                    <div>
                                        <FiCpu />
                                        <span>CNN Model</span>
                                    </div>

                                </div>

                                <div className="brain-visual-result">

                                    <div>
                                        <span>MODEL STATUS</span>
                                        <strong>Ready for Inference</strong>
                                    </div>

                                    <FiCheckCircle />

                                </div>

                            </div>

                        </Col>

                    </Row>
                </Container>
            </section>


            {/* =====================================================
                PROJECT OVERVIEW
            ===================================================== */}
            <section className="brain-stroke-section brain-stroke-overview">

                <Container>

                    <div className="brain-stroke-section-heading">

                        <span>PROJECT OVERVIEW</span>

                        <h2>
                            What is this project all about?
                        </h2>

                        <p>
                            Learn how medical images can be processed and
                            classified using computer vision and deep
                            learning techniques.
                        </p>

                    </div>

                    <Row className="align-items-center">

                        <Col lg="7">

                            <div className="brain-stroke-overview-content">

                                <div className="brain-stroke-highlight">

                                    <FiActivity />

                                    <div>
                                        <strong>
                                            Automated Brain Stroke Detection
                                        </strong>

                                        <span>
                                            MRI Image Analysis using
                                            OpenCV and Convolutional
                                            Neural Networks
                                        </span>
                                    </div>

                                </div>

                                <p>
                                    This project develops an automated system
                                    to detect brain strokes from MRI scans
                                    using deep learning. Convolutional Neural
                                    Networks (CNN) are employed for image
                                    classification, with OpenCV handling
                                    image preprocessing.
                                </p>

                                <p>
                                    The solution is built in Python for
                                    medical image analysis and early
                                    diagnosis support.
                                </p>

                            </div>

                        </Col>

                        <Col lg="5">

                            <Card className="brain-stroke-scope-card">

                                <CardBody>

                                    <div className="brain-stroke-scope-icon">
                                        <FiLayers />
                                    </div>

                                    <h3>
                                        Project Scope
                                    </h3>

                                    <div className="brain-scope-item">
                                        <FiCheckCircle />
                                        <span>
                                            MRI brain image classification
                                        </span>
                                    </div>

                                    <div className="brain-scope-item">
                                        <FiCheckCircle />
                                        <span>
                                            OpenCV preprocessing
                                        </span>
                                    </div>

                                    <div className="brain-scope-item">
                                        <FiCheckCircle />
                                        <span>
                                            CNN model training
                                        </span>
                                    </div>

                                    <div className="brain-scope-item">
                                        <FiCheckCircle />
                                        <span>
                                            Model evaluation
                                        </span>
                                    </div>

                                    <div className="brain-scope-item">
                                        <FiCheckCircle />
                                        <span>
                                            No real-time clinical deployment
                                        </span>
                                    </div>

                                </CardBody>

                            </Card>

                        </Col>

                    </Row>

                </Container>

            </section>


            {/* =====================================================
                PREREQUISITES
            ===================================================== */}
            <section className="brain-stroke-section brain-stroke-prerequisites">

                <Container>

                    <div className="brain-stroke-section-heading">

                        <span>GET READY</span>

                        <h2>
                            What You Should Know Before Starting
                        </h2>

                        <p>
                            A foundation in Python, image processing and
                            machine learning will help you get the most
                            from this project.
                        </p>

                    </div>

                    <Row>

                        {prerequisites.map((item, index) => (
                            <Col
                                md="6"
                                lg="4"
                                key={index}
                                className="mb-4"
                            >

                                <Card className="brain-stroke-info-card">

                                    <CardBody>

                                        <div className="brain-stroke-card-icon">
                                            {item.icon}
                                        </div>

                                        <h3>
                                            {item.title}
                                        </h3>

                                        <p>
                                            {item.description}
                                        </p>

                                    </CardBody>

                                </Card>

                            </Col>
                        ))}

                    </Row>

                </Container>

            </section>


            {/* =====================================================
                TECHNOLOGIES
            ===================================================== */}
            <section className="brain-stroke-section brain-stroke-technologies">

                <Container>

                    <div className="brain-stroke-section-heading">

                        <span>TECH STACK</span>

                        <h2>
                            Tools & Technologies
                        </h2>

                        <p>
                            What tools and technologies will be used
                            in this project.
                        </p>

                    </div>

                    <Row>

                        {technologies.map((technology, index) => (
                            <Col
                                sm="6"
                                md="4"
                                lg="3"
                                key={index}
                                className="mb-4"
                            >

                                <Card className="brain-stroke-tech-card">

                                    <CardBody>

                                        <div className="brain-stroke-tech-icon">
                                            {technology.icon}
                                        </div>

                                        <span>
                                            {technology.title}
                                        </span>

                                        <strong>
                                            {technology.value}
                                        </strong>

                                    </CardBody>

                                </Card>

                            </Col>
                        ))}

                    </Row>

                </Container>

            </section>


            {/* =====================================================
                LEARNING OBJECTIVES
            ===================================================== */}
            <section className="brain-stroke-section brain-stroke-objectives">

                <Container>

                    <Row className="align-items-center">

                        <Col lg="5">

                            <div className="brain-stroke-objectives-heading">

                                <span>
                                    LEARNING OBJECTIVES
                                </span>

                                <h2>
                                    What you will learn in this project
                                </h2>

                                <p>
                                    Build practical skills in medical image
                                    processing, CNN development, evaluation
                                    and AI deployment workflows.
                                </p>

                            </div>

                        </Col>

                        <Col lg="7">

                            <div className="brain-stroke-objectives-list">

                                {learningObjectives.map(
                                    (objective, index) => (
                                        <div
                                            className="brain-stroke-objective-item"
                                            key={index}
                                        >

                                            <div>
                                                <FiCheckCircle />
                                            </div>

                                            <span>
                                                {objective}
                                            </span>

                                        </div>
                                    )
                                )}

                            </div>

                        </Col>

                    </Row>

                </Container>

            </section>


            {/* =====================================================
                LESSON PLAN
            ===================================================== */}
            <section
                className="brain-stroke-section brain-stroke-curriculum"
                id="brain-stroke-curriculum"
            >

                <Container>

                    <div className="brain-stroke-section-heading">

                        <span>LESSON PLAN</span>

                        <h2>
                            Build the Project Step by Step
                        </h2>

                        <p>
                            Work through the complete pipeline from MRI
                            preprocessing to CNN model development,
                            explainability and deployment.
                        </p>

                    </div>

                    <div className="brain-stroke-timeline">

                        {modules.map((module, index) => (

                            <div
                                className={`brain-stroke-module ${module.color}`}
                                key={`${module.number}-${index}`}
                            >

                                <div className="brain-stroke-module-number">
                                    {module.number}
                                </div>

                                <Card className="brain-stroke-module-card">

                                    <CardBody>

                                        <div className="brain-stroke-module-header">

                                            <div>

                                                <span className="brain-stroke-module-label">
                                                    MODULE {module.number}
                                                </span>

                                                <h3>
                                                    {module.title}
                                                </h3>

                                            </div>

                                            <Badge color="" className="brain-stroke-duration">
                                                {module.duration}
                                            </Badge>

                                        </div>

                                        <div className="brain-stroke-module-objective">

                                            <strong>
                                                Objective:
                                            </strong>

                                            <span>
                                                {module.objective}
                                            </span>

                                        </div>

                                        <div className="brain-stroke-topic-grid">

                                            {module.topics.map(
                                                (topic, topicIndex) => (
                                                    <div
                                                        className="brain-stroke-topic"
                                                        key={topicIndex}
                                                    >
                                                        <FiCheckCircle />

                                                        <span>
                                                            {topic}
                                                        </span>
                                                    </div>
                                                )
                                            )}

                                        </div>

                                        <div className="brain-stroke-deliverable">

                                            <FiZap />

                                            <div>

                                                <strong>
                                                    Deliverable
                                                </strong>

                                                <span>
                                                    {module.deliverable}
                                                </span>

                                            </div>

                                        </div>

                                    </CardBody>

                                </Card>

                            </div>

                        ))}

                    </div>

                </Container>

            </section>


            {/* =====================================================
                FAQ
            ===================================================== */}
            <section className="brain-stroke-section brain-stroke-faq">

                <Container>

                    <div className="brain-stroke-section-heading">

                        <span>FAQ</span>

                        <h2>
                            Frequently Asked Questions
                        </h2>

                        <p>
                            Everything you need to know before starting
                            the Brain Stroke Prediction project.
                        </p>

                    </div>

                    <div className="brain-stroke-faq-wrapper">

                        <Accordion
                            open={openFaq}
                            toggle={toggleFaq}
                        >

                            {faqs.map((faq) => (

                                <AccordionItem key={faq.id}>

                                    <AccordionHeader targetId={faq.id}>
                                        {faq.question}
                                    </AccordionHeader>

                                    <AccordionBody accordionId={faq.id}>

                                        {faq.answer}

                                        {faq.id === "faq-8" && (
                                            <p className="brain-stroke-faq-link">
                                                Please visit our page:{" "}

                                                <a
                                                    href="https://wocons.com/"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    WoCons – Custom AI &
                                                    CAD Solutions | Bengaluru
                                                </a>
                                            </p>
                                        )}

                                    </AccordionBody>

                                </AccordionItem>

                            ))}

                        </Accordion>

                    </div>

                </Container>

            </section>


            {/* =====================================================
                RELATED PROJECTS
            ===================================================== */}
            <section className="brain-stroke-section brain-stroke-related">

                <Container>

                    <div className="brain-stroke-section-heading">

                        <span>EXPLORE MORE</span>

                        <h2>
                            Explore More AI Projects
                        </h2>

                        <p>
                            Continue exploring practical AI and machine
                            learning projects.
                        </p>

                    </div>

                    <Row>

                        {relatedProjects.map(
                            (project, index) => (

                                <Col
                                    lg="6"
                                    key={index}
                                    className="mb-4"
                                >

                                    <Card className="brain-stroke-related-card">

                                        <CardBody>

                                            <div className="brain-stroke-related-icon">
                                                {project.icon}
                                            </div>

                                            <h3>
                                                {project.title}
                                            </h3>

                                            <p>
                                                {project.description}
                                            </p>

                                            <a
                                                href={project.link}
                                                className="brain-stroke-related-link"
                                            >
                                                View Course Page
                                                <FiArrowRight />
                                            </a>

                                        </CardBody>

                                    </Card>

                                </Col>

                            )
                        )}

                    </Row>

                </Container>

            </section>


            {/* =====================================================
                CONTACT
            ===================================================== */}
            <section
                className="brain-stroke-contact"
                id="brain-stroke-contact"
            >

                <Container>

                    <div className="brain-stroke-contact-heading">

                        <span>
                            READY TO BUILD?
                        </span>

                        <h2>
                            Let's Discuss Your Brain Stroke AI Project
                        </h2>

                        <p>
                            Have questions about the project, tools,
                            prerequisites, or training? Send us your
                            details and our team will get back to you.
                        </p>

                    </div>

                    <div className="brain-stroke-contact-wrapper">

                        <GetInTouch
                            courseName="Brain Stroke Prediction using OpenCV and CNN"
                            title="Get in Touch"
                            subtitle="Interested in building this medical image analysis project? Send us your details and our team will contact you."
                        />

                    </div>

                </Container>

            </section>

        </div>
</>

    );
};

export default BrainStrokePredictionProjectPage;